import React, { useContext, useMemo } from 'react'
import AlwaysOnRoomOptionMobile from './AlwaysOnRoomOptionMobile'
import LayoutContainer from 'components/Common/LayoutContainer'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getDurationPackageOptions,
  getLowestAvailableStayPayTier,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { buildRoomPackageOptionsViewOrderLPC, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { initialRooms } from 'lib/offer/occupancyUtils'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Plural from 'components/utils/Formatters/Plural'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import HotelFlightsToggleButton from 'components/OfferPage/Accommodation/HotelFlightsToggle/HotelFlightsToggleButton'

interface Props {
  offer: App.Offer;
  rooms: Array<App.Occupants>;
  checkIn?: string;
  packageOptions: Array<OfferPackageOptionView>;
  roomOption?: OfferPageRoomOption;
  showFlightInformation?: boolean;
  lowestAvailableStayPayTier?: App.StayPayTier | null;
}

function AlwaysOnRoomOptionsMobile(props: Props) {
  const {
    offer,
    packageOptions,
    showFlightInformation,
    roomOption,
    checkIn,
    rooms,
    lowestAvailableStayPayTier,
  } = props

  const supportContact = useContext(ContactContext)
  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  const packageDuration = packageOptionsByRoom?.[0]?.packageViews?.[0]?.package?.duration
  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(packageDuration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, packageDuration])

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialRooms,
  })

  const hasRateError = !!offerAvailableRates?.error

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      <VerticalSpacer gap={16}>
        <Heading variant="heading4">Room Options</Heading>
        <HotelFlightsToggleButton />
        {hasRateError && <MessageBanner
          kind="warning"
          tabletAlign="center"
          heading={`The third party booking system for ${offer.property?.name} is currently down`}
          description={`Please call our VIP team on ${supportContact.local.humanReadable} who are ready to provide assistance to complete your booking`}
        />}
      </VerticalSpacer>

    </LayoutContainer>
    {!hasRateError && (
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
          description={
            <>
              <b>Special offer:</b> Enjoy free nights for stays longer than <Plural count={lowestAvailableStayPayTier.stayNights} singular="day" withCount />. Select dates to check eligibility.
            </>
          }
        />}
        <Pane background="neutral-six">
          <VerticalSpacer gap={8}>
            {packageOptionsByRoom.map((item) => {
              if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
                return null
              }
              return (<AlwaysOnRoomOptionMobile
                key={item.roomId}
                offer={offer}
                packageViews={item.packageViews}
                detailsModalMode="room-and-package"
              />)
            })}
          </VerticalSpacer>
        </Pane>
      </VerticalSpacer>
    )}
    <LayoutContainer>
      {showFlightInformation && <Caption variant="medium" align="center">
        *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
      </Caption>}
    </LayoutContainer>
  </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  checkIn: state.checkInDate,
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnRoomOptionsMobile, preventRenderWithOpenFilter)
